import { normalizeYearMonth } from "../legacy/utils.js";
import type { CurrentUser } from "../state/slices/user.ts";
import type { NamedOpening, StudyColor, StudyKind, StudyListItem } from "../domain/studyList.ts";
import type { DrillPosition, DrillResult, ExerciseCount, GamePhase, PositionExercise } from "../domain/positions.ts";
import type { AdminUserList } from "../domain/adminUsers.ts";
import type { AdminMetrics } from "../domain/adminMetrics.ts";

/** Anything that can go into a query string. Null and undefined are dropped by the builders. */
export type QueryValue = string | number | boolean | null | undefined;

/** An entity id as the server hands it back. */
export type Id = number | string;

/** Filters shared by the game import and the player statistics endpoints. */
export interface PlayerFilters {
    fen?: string;
    color?: string;
    speed?: string;
    mode?: string;
    minElo?: QueryValue;
    maxElo?: QueryValue;
    since?: QueryValue;
    until?: QueryValue;
    depth?: QueryValue;
}

export interface ImportRequest {
    username: string;
    since?: QueryValue;
    until?: QueryValue;
    color?: string;
    speeds?: string[];
    platform?: string;
}

export function buildBookQueryParams(fen: string, formData: FormData, extra: Record<string, QueryValue> = {}) {
  const speeds = formData.getAll("speeds");
  const ratings = formData.getAll("ratings");
  const params = new URLSearchParams();
  params.set("fen", fen);
  params.set("since", normalizeYearMonth(formData.get("since")));
  params.set("until", normalizeYearMonth(formData.get("until")));
  if (speeds.length) {
    params.set("speeds", speeds.join(","));
  }
  if (ratings.length) {
    params.set("ratings", ratings.join(","));
  }

  Object.entries(extra).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    let normalized = value;
    if (typeof value === "string") {
      normalized = value.trim();
      if (!normalized) {
        return;
      }
    }
    params.set(key, String(normalized));
  });

  return params;
}

export async function fetchBookMoves(fen: string, formData: FormData, extraParams: Record<string, QueryValue> = {}) {
  const params = buildBookQueryParams(fen, formData, extraParams);
  const response = await fetch(`/api/book?${params.toString()}`);
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      try { return await response.json(); } catch { return null; }
    }
    return null;
  }
  return response.json();
}

/**
 * The explorer at one position, with no filters: what players do here. It is what a paused game
 * shows, where there is no settings form to read filters from. 401 comes back as the answer the
 * book always gives without a linked Lichess account, for the caller to show instead of failing.
 */
export async function fetchBookAt(fen: string) {
  const response = await fetch(`/api/book?fen=${encodeURIComponent(fen)}`);
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      return { error: "lichess_auth_required" };
    }
    throw new Error(`Failed to read the book: HTTP ${response.status}`);
  }
  return response.json();
}

/** The rating bands, speeds and dates one explorer request is made with. */
export interface BookFilters {
  /** Lichess explorer rating bands. Ignored for the masters database, which has none. */
  ratings?: string[];
  speeds?: string[];
  since?: string;
  until?: string;
  /** Ask the masters database instead of rated games. */
  masters?: boolean;
}

/**
 * The explorer at one position, for a level: the rating bands come from the level chosen, the
 * speeds and dates from the saved configuration. It is what a sparring game asks when it wants the
 * moves real players made, and the one caller that has no settings form to read filters from.
 *
 * 401 comes back as the answer the book gives without a linked Lichess account, for the caller to
 * do something else about instead of failing.
 */
export async function fetchBookForLevel(fen: string, filters: BookFilters = {}) {
  const params = new URLSearchParams();
  params.set("fen", fen);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  if (!filters.masters) {
    if (filters.ratings?.length) params.set("ratings", filters.ratings.join(","));
    if (filters.speeds?.length) params.set("speeds", filters.speeds.join(","));
  }
  const path = filters.masters ? "/api/book/masters" : "/api/book";
  const response = await fetch(`${path}?${params.toString()}`);
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      return { error: "lichess_auth_required" };
    }
    throw new Error(`Failed to read the book: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchMastersBookMoves(fen: string, formData: FormData) {
  const params = new URLSearchParams();
  if (fen) {
    params.set("fen", fen);
  }
  if (formData) {
    const since = formData.get("since");
    const until = formData.get("until");
    if (since) params.set("since", String(since));
    if (until) params.set("until", String(until));
  }
  const response = await fetch(`/api/book/masters?${params.toString()}`);
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      try { return await response.json(); } catch { return null; }
    }
    return null;
  }
  return response.json();
}

export async function importLichessGames({ username, since, until, color, speeds, platform }: ImportRequest) {
  const params = new URLSearchParams();
  params.set("player", username);
  if (platform) params.set("platform", platform);
  if (since) params.set("since", String(since));
  if (until) params.set("until", String(until));
  if (color) params.set("color", color);
  if (speeds) params.set("speeds", String(speeds));

  const response = await fetch(`/api/player/import?${params.toString()}`, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to import games.");
  }
  if (response.status === 400) {
    throw new Error("Please enter a username.");
  }
  if (!response.ok) {
    throw new Error(`Import failed: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchPlayerNextMoves({ fen, color, speed, mode, minElo, maxElo, since, until }: PlayerFilters) {
  const params = new URLSearchParams();
  if (fen) params.set("fen", fen);
  if (color) params.set("color", color);
  if (speed) params.set("speed", speed);
  if (mode) params.set("mode", mode);
  if (minElo) params.set("minElo", String(minElo));
  if (maxElo) params.set("maxElo", String(maxElo));
  if (since) params.set("since", String(since));
  if (until) params.set("until", String(until));

  const response = await fetch(`/api/player/moves?${params.toString()}`, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to view your game moves.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch moves: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchPlayerPlans({ fen, color, depth, speed, mode, minElo, maxElo, since, until }: PlayerFilters) {
  const params = new URLSearchParams();
  if (fen) params.set("fen", fen);
  if (color) params.set("color", color);
  if (depth) params.set("depth", String(depth));
  if (speed) params.set("speed", speed);
  if (mode) params.set("mode", mode);
  if (minElo) params.set("minElo", String(minElo));
  if (maxElo) params.set("maxElo", String(maxElo));
  if (since) params.set("since", String(since));
  if (until) params.set("until", String(until));

  const response = await fetch(`/api/player/moves/plans?${params.toString()}`, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to view plans.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch plans: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchLichessStatus() {
  const response = await fetch("/api/lichess/status", { credentials: "include" });
  if (!response.ok) {
    return { connected: false };
  }
  return response.json();
}

export async function disconnectLichess() {
  const response = await fetch("/api/lichess/disconnect", {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to disconnect Lichess: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchAuthMe(): Promise<CurrentUser | null> {
  const response = await fetch("/api/auth/me", { credentials: "include" });
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function logout() {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
  });
}

export async function loadUserConfig() {
  const response = await fetch("/api/user/config", { credentials: "include" });
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function saveUserConfig(payload: unknown) {
  const response = await fetch("/api/user/config", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response;
}

export async function uploadRepertoire(name: string, pgn: string) {
  const response = await fetch("/api/repertoire", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, pgn }),
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to upload repertoires.");
  }
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Upload failed with status ${response.status}`);
  }
  return response.json();
}

export async function fetchRepertoires() {
  const response = await fetch("/api/repertoire", {
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to view repertoires.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch repertoires: HTTP ${response.status}`);
  }
  return response.json();
}

export async function deleteRepertoire(id: Id) {
  const response = await fetch(`/api/repertoire/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to delete repertoires.");
  }
  if (response.status === 404) {
    throw new Error("Repertoire not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to delete repertoire: HTTP ${response.status}`);
  }
}

export async function toggleRepertoire(id: Id) {
  const response = await fetch(`/api/repertoire/${id}/toggle`, {
    method: "POST",
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to toggle repertoires.");
  }
  if (response.status === 404) {
    throw new Error("Repertoire not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to toggle repertoire: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * The studies of a Lichess account: the connected one, or anyone's public studies by username.
 * `canReadPrivate` says whether the list could hold private and unlisted studies at all.
 */
export async function fetchLichessStudies(username?: string | null) {
  const query = username ? `?username=${encodeURIComponent(username)}` : "";
  const response = await fetch(`/api/repertoire/studies${query}`, { credentials: "include" });
  return studyJson(response, "Failed to list studies");
}

/** What a study would become, without saving it. */
export async function previewLichessStudy(study: string) {
  const response = await fetch("/api/repertoire/studies/preview", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ study }),
  });
  return studyJson(response, "Failed to read the study");
}

/** Saves the study as a repertoire of this account. */
export async function importLichessStudy(study: string, name?: string | null) {
  const response = await fetch("/api/repertoire/studies", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ study, name: name || null }),
  });
  return studyJson(response, "Failed to import the study");
}

/** The server explains these failures in words meant for the user, so they are read back. */
async function studyJson(response: Response, fallback: string) {
  if (response.ok) {
    return response.json();
  }
  if (response.status === 401) {
    throw new Error("You must be logged in to import a study.");
  }
  const body = await response.json().catch(() => null);
  throw new Error(body?.message || `${fallback}: HTTP ${response.status}`);
}

export async function fetchRepertoireNext(params: URLSearchParams | Record<string, string>) {
  const query = params.toString();
  const url = query ? `/api/repertoire/next?${query}` : "/api/repertoire/next";
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchGameImports(player?: string | null) {
  const params = new URLSearchParams();
  if (player) params.set("player", player);

  const response = await fetch(`/api/player/imports?${params.toString()}`, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to view imports.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch imports: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Asks for an import to go. The answer comes back as soon as it is marked, not when it is gone: its
 * games are removed in batches in the background, which on a big import takes a while.
 */
export async function deleteGameImport(importId: Id) {
  const response = await fetch(`/api/player/imports/${importId}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to delete imports.");
  }
  if (response.status === 404) {
    throw new Error("Import not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to delete import: HTTP ${response.status}`);
  }
  return response.status === 204 ? null : response.json().catch(() => null);
}

export async function refreshGameImport(importId: Id) {
  const response = await fetch(`/api/player/imports/${importId}/refresh`, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to refresh imports.");
  }
  if (response.status === 404) {
    throw new Error("Import not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to refresh import: HTTP ${response.status}`);
  }
  return response.json();
}

export async function toggleGameImport(importId: Id) {
  const response = await fetch(`/api/player/imports/${importId}/toggle`, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to toggle imports.");
  }
  if (response.status === 404) {
    throw new Error("Import not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to toggle import: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Start an async import job. Returns immediately with the import info.
 * Use getImportStatus to poll for progress.
 */
export async function startAsyncImport({ username, since, until, color, speeds, platform }: ImportRequest) {
  const params = new URLSearchParams();
  params.set("player", username);
  if (platform) params.set("platform", platform);
  if (since) params.set("since", String(since));
  if (until) params.set("until", String(until));
  if (color) params.set("color", color);
  if (speeds) params.set("speeds", String(speeds));

  const response = await fetch(`/api/player/import/async?${params.toString()}`, {
    method: "POST",
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to import games.");
  }
  if (response.status === 400) {
    throw new Error("Please enter a username.");
  }
  if (!response.ok) {
    throw new Error(`Import failed: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Get the current status of an import job.
 */
export async function getImportStatus(importId: Id) {
  const response = await fetch(`/api/player/imports/${importId}/status`, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("You must be logged in to check import status.");
  }
  if (response.status === 404) {
    throw new Error("Import not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to get import status: HTTP ${response.status}`);
  }
  return response.json();
}

// ============================================
// Lines API
// ============================================

/** The user's lines; `all` adds the ones studied once and not kept, which no list shows. */
export async function fetchLines(all = false) {
  const response = await fetch(all ? "/api/lines?all=true" : "/api/lines", {
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to view lines.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch lines: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchActiveLines() {
  const response = await fetch("/api/lines/active", {
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to view lines.");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch active lines: HTTP ${response.status}`);
  }
  return response.json();
}

/** `origin` says whether the user built this line or clicked a row of their games to study it. */
/** A line studied once becomes one of the user's. */
export async function keepLine(id: number) {
  const response = await fetch(`/api/lines/${id}/keep`, { method: "POST", credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to keep the line: HTTP ${response.status}`);
  }
  return response.json();
}

export async function createLine(name: string, fen: string, uciMoves: string, color: string,
                                 origin: "manual" | "games" | "once" = "manual") {
  const response = await fetch("/api/lines", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, fen, uciMoves, color, origin }),
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to create lines.");
  }
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Failed to create line: HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateLine(id: Id, name: string, fen: string, uciMoves: string, color: string) {
  const response = await fetch(`/api/lines/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, fen, uciMoves, color }),
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to update lines.");
  }
  if (response.status === 404) {
    throw new Error("Line not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to update line: HTTP ${response.status}`);
  }
  return response.json();
}

export async function deleteLine(id: Id) {
  const response = await fetch(`/api/lines/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to delete lines.");
  }
  if (response.status === 404) {
    throw new Error("Line not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to delete line: HTTP ${response.status}`);
  }
}

export async function toggleLine(id: Id) {
  const response = await fetch(`/api/lines/${id}/toggle`, {
    method: "POST",
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to toggle lines.");
  }
  if (response.status === 404) {
    throw new Error("Line not found.");
  }
  if (!response.ok) {
    throw new Error(`Failed to toggle line: HTTP ${response.status}`);
  }
  return response.json();
}

// ============================================
// Onboarding API
// ============================================

export async function fetchOnboardingStatus() {
  const response = await fetch("/api/onboarding/status", {
    credentials: "include",
  });
  if (response.status === 401) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch onboarding status: HTTP ${response.status}`);
  }
  return response.json();
}

export async function dismissOnboarding() {
  const response = await fetch("/api/onboarding/dismiss", {
    method: "POST",
    credentials: "include",
  });
  if (response.status === 401) {
    throw new Error("You must be logged in to dismiss onboarding.");
  }
  if (!response.ok) {
    throw new Error(`Failed to dismiss onboarding: HTTP ${response.status}`);
  }
  return response.json();
}

// ============================================
// Welcome: connecting where the user plays
// ============================================

/** chess.com needs no OAuth: the username is checked and its year of games starts coming down. */
export async function connectChesscom(username: string) {
  const response = await fetch("/api/onboarding/providers/chesscom", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.message || `Could not connect chess.com: HTTP ${response.status}`);
  }
  return response.json();
}

/** The year of games of a Lichess account that is already linked. */
export async function startLichessBackfill() {
  const response = await fetch("/api/onboarding/providers/lichess/import", {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.message || `Could not import your Lichess games: HTTP ${response.status}`);
  }
  return response.json();
}

/** Another Lichess account, by username. The first one is the OAuth link; this is every one after it. */
export async function addLichessAccount(username: string) {
  const response = await fetch("/api/onboarding/providers/lichess", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.message || `Could not add that Lichess account: HTTP ${response.status}`);
  }
  return response.json();
}

/** Every account the user added, on either platform, with how its download is going. */
export async function fetchOnboardingAccounts() {
  const response = await fetch("/api/onboarding/accounts", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not read your accounts: HTTP ${response.status}`);
  }
  return response.json();
}

/** The openings of the games that came in after `after`: the page adds them up as they arrive. */
export async function fetchImportedOpenings(after: number) {
  const response = await fetch(`/api/onboarding/openings?after=${after}`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not read your openings: HTTP ${response.status}`);
  }
  return response.json();
}

/** What each account is rated at blitz and rapid, as its platform says. */
export async function fetchAccountRatings() {
  const response = await fetch("/api/onboarding/ratings", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not read your ratings: HTTP ${response.status}`);
  }
  return response.json();
}

/** Wins, draws and losses of the last twelve months, for each colour. */
export async function fetchColorRecord() {
  const response = await fetch("/api/onboarding/record", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not read your results: HTTP ${response.status}`);
  }
  return response.json();
}

export async function saveWelcomeStep(step: string) {
  const response = await fetch("/api/onboarding/step", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step }),
  });
  if (!response.ok) {
    throw new Error(`Could not save your progress: HTTP ${response.status}`);
  }
  return response.json();
}

/** The welcome's survey as it was answered, or null before it was. */
export async function fetchPlayerSurvey(): Promise<{ answers: Record<string, string | string[]>; skipped: boolean } | null> {
  const response = await fetch("/api/onboarding/survey", { credentials: "include" });
  if (response.status === 204 || response.status === 401) return null;
  if (!response.ok) throw new Error(`Could not read the survey: HTTP ${response.status}`);
  return response.json();
}

/** Answers the survey, or skips it: either way the step is done. Answers with the welcome's status. */
export async function savePlayerSurvey(answers: Record<string, string | string[]>, skipped: boolean) {
  const response = await fetch("/api/onboarding/survey", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ answers, skipped }),
  });
  if (!response.ok) throw new Error(`Could not save the survey: HTTP ${response.status}`);
  return response.json();
}

/** Seen through to the end: the dashboard stops inviting to it. Leaving it for later is saveWelcomeStep("later"). */
export async function completeWelcome() {
  const response = await fetch("/api/onboarding/complete", {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Could not close the welcome: HTTP ${response.status}`);
  }
  return response.json();
}

// === Practice Sessions ===

export async function startPracticeSession(userLineId: Id | null, lineName: string, userColor: string, startingFen: string) {
  const response = await fetch("/api/practice/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userLineId, lineName, userColor, startingFen }),
  });
  if (!response.ok) {
    throw new Error(`Failed to start practice session: HTTP ${response.status}`);
  }
  return response.json();
}

export async function endPracticeSession(sessionId: Id, completed: boolean, deviated: boolean, moves: unknown[]) {
  const response = await fetch(`/api/practice/sessions/${sessionId}/end`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ completed, deviated, moves }),
  });
  if (!response.ok) {
    throw new Error(`Failed to end practice session: HTTP ${response.status}`);
  }
  return response.json();
}

export function endPracticeSessionBeacon(sessionId: Id, completed: boolean, deviated: boolean, moves: unknown[]) {
  const body = JSON.stringify({ completed, deviated, moves });
  navigator.sendBeacon(
    `/api/practice/sessions/${sessionId}/end`,
    new Blob([body], { type: "application/json" })
  );
}

export async function fetchPracticeSessions({ lineId, page = 0, size = 50 }: { lineId?: Id; page?: number; size?: number } = {}) {
  const params = new URLSearchParams();
  if (lineId) params.set("lineId", String(lineId));
  params.set("page", String(page));
  params.set("size", String(size));
  const response = await fetch(`/api/practice/sessions?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch practice sessions: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchPracticeSession(sessionId: Id) {
  const response = await fetch(`/api/practice/sessions/${sessionId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch practice session: HTTP ${response.status}`);
  }
  return response.json();
}

/** Opening positions the user reaches most in their imported games, with how they score there. */
export async function fetchSuggestedLines() {
  const response = await fetch("/api/player/lines/suggested");
  if (!response.ok) {
    throw new Error(`Failed to fetch suggested lines: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Both ends of that same ranking for the dashboard: where the user scores best and where they score
 * worst, over the last `days`. The answer says which window it really used — a month of games is
 * often too little, and the server widens it to the year rather than answering with nothing.
 */
/** `deep` lowers the bar a line has to clear, which is what "show more lines" asks for. */
/** `widen` lets the server fall back to the year when the window holds nothing. Off once the
    reader has chosen a window: they asked for those days and those days are the answer. */
export async function fetchLineHighlights(days = 30, limit = 3, widen = true) {
  const response = await fetch(`/api/player/lines/highlights?days=${days}&limit=${limit}&widen=${widen}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch line highlights: HTTP ${response.status}`);
  }
  return response.json();
}

/** The whole stored line tree of the user, worst first. */
export async function fetchLineTree(deep = false) {
  const response = await fetch(`/api/player/lines/tree?deep=${deep}`, { credentials: "include" });
  if (response.status === 401) {
    return [];
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch your lines: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * One position of the user's tree and what they played from it. `null` when nothing is stored there,
 * which is an answer and not a failure: it is a position they have not reached often enough.
 */
export async function fetchLineNode(color: string, line = "",
                                   filter: { since?: string | null; until?: string | null; result?: string | null } = {}) {
  const query = new URLSearchParams({ color });
  if (line) query.set("line", line);
  // Nothing of these is every game they hold, which is the tree that is already worked out and stored
  if (filter.since) query.set("since", filter.since);
  if (filter.until) query.set("until", filter.until);
  if (filter.result && filter.result !== "all") query.set("result", filter.result);
  const response = await fetch(`/api/player/lines/node?${query}`, { credentials: "include" });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch that position: HTTP ${response.status}`);
  }
  return response.json();
}

/** How one line or variation has gone, month by month. */
export async function fetchLineHistory(color: string, line: string, months = 12) {
  const query = new URLSearchParams({ color, line, months: String(months) });
  const response = await fetch(`/api/player/lines/history?${query}`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to fetch the history of that line: HTTP ${response.status}`);
  }
  return response.json();
}

/** The last games of each colour, with the opening they were and the position they came out in. */
export async function fetchRecentGames(limit = 10) {
  const response = await fetch(`/api/player/games/recent?limit=${limit}`, { credentials: "include" });
  if (response.status === 401) {
    return { white: [], black: [] };
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch your last games: HTTP ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------------------------
// The recommendation of the day
// ---------------------------------------------------------------------------------------------

/** Saves a finished practice in one call; safe to repeat, the server recognises the practice id. */
export async function savePracticeSession(practice: unknown) {
  const response = await fetch("/api/practice/sessions/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(practice),
  });
  if (!response.ok) {
    throw new Error(`Failed to save the practice: HTTP ${response.status}`);
  }
  return response.json();
}

/** Moves the practices a visitor made on this device into the account they just logged into. */
export async function importPracticeSessions(practices: unknown[]) {
  const response = await fetch("/api/practice/sessions/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ practices }),
  });
  if (!response.ok) {
    const error = new Error(`Failed to import practices: HTTP ${response.status}`) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return response.json();
}

/** Practice log of the account, in the shape progressRules.ts works with. */
export async function fetchPracticeProgress() {
  const response = await fetch("/api/practice/progress");
  if (!response.ok) {
    throw new Error(`Failed to fetch progress: HTTP ${response.status}`);
  }
  return response.json();
}

// --- Admin panel (requires an admin account) ---

async function fetchAdminJson(path: string) {
  const response = await fetch(path, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Admin request failed: HTTP ${response.status}`);
  }
  return response.json();
}

// The diagnosis lab (admin, DiagnosisLabController): a game as the worker reads it, and what is kept of its positions

export function fetchLabGame(gameId: number) {
  return fetchAdminJson(`/api/admin/diagnosis/lab/games/${gameId}`);
}

export function fetchLabGames(userId: number, { cause = "", limit = 100 }: { cause?: string; limit?: number } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cause) params.set("cause", cause);
  return fetchAdminJson(`/api/admin/diagnosis/lab/users/${userId}/games?${params}`);
}

export function fetchLabPositions(keys: string[]) {
  return sendAdminJson("POST", "/api/admin/diagnosis/lab/positions", { keys });
}

/** A change made in the admin panel: the answer, or the server's own words for why not. */
async function sendAdminJson(method: string, path: string, body: unknown) {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const answer = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((answer?.message as string) || `Admin request failed: HTTP ${response.status}`);
  }
  return answer;
}

// What each user may have analysed, and their subscription (docs/GAME-DIAGNOSIS.md §6)
export function fetchAdminAnalysisAccounts({ q = "", page = 0, size = 50 }: { q?: string; page?: number; size?: number } = {}) {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (q) params.set("q", q);
  return fetchAdminJson(`/api/admin/diagnosis/accounts?${params}`);
}

export function fetchAdminAnalysisAccount(userId: Id) {
  return fetchAdminJson(`/api/admin/diagnosis/accounts/${userId}`);
}

// The emails sent and what became of them, and the addresses nothing goes to (the "Emails" tab)
export interface AdminSentEmail {
  id: number;
  kind: string;
  to: string;
  subject: string | null;
  status: string;
  detail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSuppressedEmail {
  email: string;
  reason: string;
  detail: string | null;
  createdAt: string;
}

export interface AdminEmails {
  sent: AdminSentEmail[];
  suppressed: AdminSuppressedEmail[];
}

export function fetchAdminEmails(q = "", limit = 100): Promise<AdminEmails> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (q) params.set("q", q);
  return fetchAdminJson(`/api/admin/emails?${params}`);
}

export function liftEmailSuppression(email: string): Promise<AdminEmails> {
  return sendAdminJson("DELETE", `/api/admin/emails/suppressions?${new URLSearchParams({ email })}`, null);
}

// The analysis queue as it stands: what runs, what waits behind it, what failed (the "Queue" tab)
export function fetchAdminDiagnosisQueue() {
  return fetchAdminJson("/api/admin/diagnosis/queue");
}

/** Paid games given, or taken back with a negative number. */
export function grantAdminPaidGames(userId: Id, games: number) {
  return sendAdminJson("POST", `/api/admin/diagnosis/accounts/${userId}/paid`, { games });
}

export function setAdminFreeGames(userId: Id, freeLeft: number) {
  return sendAdminJson("PUT", `/api/admin/diagnosis/accounts/${userId}/free`, { freeLeft });
}

/** The last day of the subscription, yyyy-MM-dd; null ends it. */
export function setAdminSubscription(userId: Id, until: string | null) {
  return sendAdminJson("PUT", `/api/admin/diagnosis/accounts/${userId}/subscription`, { until });
}

export function fetchAdminSummary() {
  return fetchAdminJson("/api/admin/summary");
}

/** The user list: `params` is domain/adminUsers.ts:queryParams, which the CSV link shares. */
export function fetchAdminUsers(params: URLSearchParams, page = 0, size = 50): Promise<AdminUserList> {
  const query = new URLSearchParams(params);
  query.set("page", String(page));
  query.set("size", String(size));
  return fetchAdminJson(`/api/admin/users?${query}`);
}

export function fetchAdminUser(userId: Id) {
  return fetchAdminJson(`/api/admin/users/${userId}`);
}

export function fetchAdminUserPracticeSessions(userId: Id, { page = 0, size = 20 }: { page?: number; size?: number } = {}) {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  return fetchAdminJson(`/api/admin/users/${userId}/practice-sessions?${params}`);
}

/** The "Metrics" tab: `days` of engagement, and the sign-ups of the last `activationDays`. */
export function fetchAdminMetrics(days = 90, activationDays = 30): Promise<AdminMetrics> {
  const params = new URLSearchParams({ days: String(days), activationDays: String(activationDays) });
  return fetchAdminJson(`/api/admin/metrics?${params}`);
}

export function fetchAdminTrends(weeks: number = 12) {
  return fetchAdminJson(`/api/admin/trends?${new URLSearchParams({ weeks: String(weeks) })}`);
}

export function fetchAdminFunnel(days: number = 30) {
  return fetchAdminJson(`/api/admin/funnel?${new URLSearchParams({ days: String(days) })}`);
}

export function fetchAdminActivity(limit: number = 50) {
  return fetchAdminJson(`/api/admin/activity?${new URLSearchParams({ limit: String(limit) })}`);
}

export function fetchAdminFailedImports(limit: number = 50) {
  return fetchAdminJson(`/api/admin/imports/failed?${new URLSearchParams({ limit: String(limit) })}`);
}

/** The nightly refresh of imports: the last nights and the imports of the newest one. */
export function fetchAdminNightlyImports(nights: number = 14) {
  return fetchAdminJson(`/api/admin/imports/nightly?${new URLSearchParams({ nights: String(nights) })}`);
}

/** The imports of one night, the failed ones first. */
export function fetchAdminNightlyImport(runId: number) {
  return fetchAdminJson(`/api/admin/imports/nightly/${runId}`);
}

/** Rebuilding every stored opening tree (LineTreeRebuild): how the last one went, or the one running. */
export function fetchAdminTreeRebuild() {
  return fetchAdminJson("/api/admin/lines/rebuild-all");
}

/** Starts rebuilding every tree in the background; one already running is answered as it is. */
export function startAdminTreeRebuild() {
  return sendAdminJson("POST", "/api/admin/lines/rebuild-all", {});
}

// --- Account: the calls the sign-up, login, reset and verification pages make ---

export interface AuthResult {
    success: boolean;
    message?: string;
    userId?: number;
    /** The answer opened a session too (registering where the verification email is skipped). */
    signedIn?: boolean;
}

const postJson = async (url: string, body?: unknown): Promise<AuthResult> => {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    return response.json() as Promise<AuthResult>;
};

export const login = (email: string, password: string) => postJson("/api/auth/login", { email, password });

export const register = (email: string, password: string, displayName: string) =>
    postJson("/api/auth/register", { email, password, displayName });

export const forgotPassword = (email: string) => postJson("/api/auth/forgot-password", { email });

export const resetPassword = (token: string, newPassword: string) =>
    postJson("/api/auth/reset-password", { token, newPassword });

export const verifyEmail = (token: string) =>
    postJson(`/api/auth/verify-email?token=${encodeURIComponent(token)}`);

export const resendVerification = (email: string) => postJson("/api/auth/resend-verification", { email });

// -------------------------------------------------------------------------------------------------
// Why you lose (/api/diagnosis, docs/GAME-DIAGNOSIS.md §4.1): reads of what the worker analysed
// -------------------------------------------------------------------------------------------------

/**
 * What every part of the report is about: one colour or both, one result or all of them; the rest of the
 * filter keeps its defaults.
 */
export interface DiagnosisFilter {
  color?: "white" | "black" | null;
  result?: "loss" | "draw" | "win" | null;
  /** Speeds, comma separated, or "all"; nothing is every speed but bullet. */
  speed?: string | null;
  provider?: "lichess" | "chesscom" | null;
  /**
   * Where the games read start: a day (yyyy-MM-dd, the home's "lately") or an instant (ISO-8601, which is
   * what the analysis sends — its week opens at midnight in the report's own clock, not UTC's).
   */
  since?: string | null;
  /** Where they end: a day, included, or an instant, excluded. */
  until?: string | null;
}

/** The part of a filter every read takes: everything but what a card asks for on its own. */
const scopeOf = (filter: DiagnosisFilter) => ({
  color: filter.color, result: filter.result, speed: filter.speed, provider: filter.provider, since: filter.since,
  until: filter.until,
});

export interface DiagnosisGamesQuery extends DiagnosisFilter {
  cause?: string | null;
  kind?: string | null;
  exit?: string | null;
  opening?: string | null;
  /** The first moves in SAN: one branch of the opening, which then is that name alone. */
  moves?: string | null;
  /** The games nobody analysed too. */
  every?: boolean;
  page?: number;
  size?: number;
}

async function diagnosisJson(path: string, params: Record<string, string | number | boolean | null | undefined>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") query.set(key, String(value));
  });
  const response = await fetch(`/api/diagnosis/${path}${query.size ? `?${query}` : ""}`, { credentials: "include" });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch the report: HTTP ${response.status}`);
  }
  return response.json();
}

export function fetchDiagnosisSummary(filter: DiagnosisFilter) {
  return diagnosisJson("summary", scopeOf(filter));
}

export function fetchDiagnosisOpenings(filter: DiagnosisFilter) {
  return diagnosisJson("openings", scopeOf(filter));
}

/** The facts of the analysed games, added up, and the advantages that slipped away (docs/GAME-DIAGNOSIS.md §3.3.1). */
export function fetchDiagnosisFacts(filter: DiagnosisFilter) {
  return diagnosisJson("facts", scopeOf(filter));
}

/** How each phase went, yours and your opponents', over the analysed games. */
export function fetchDiagnosisPhases(filter: DiagnosisFilter) {
  return diagnosisJson("phases", scopeOf(filter));
}

/** Your last analysed games of each speed and site (500: the board reads 100, the history the rest): not the window of the page. */
export function fetchDiagnosisLevels(limit = 500, depth?: number) {
  return diagnosisJson("levels", { limit, depth });
}

/** Where the points of each board go, as the last analysis left them (docs/FINDINGS.md): the busiest board first. */
export function fetchFindings() {
  return diagnosisJson("findings", {});
}

/** How much of each speed and site your level can read: imported, analysed at the depth, and the new ones not yet. */
export function fetchLevelCoverage(depth?: number) {
  return diagnosisJson("levels/coverage", { depth });
}

/** Every analysed game's opening with its first moves: what a name is split by, below the names. */
export function fetchDiagnosisOpeningLines(filter: DiagnosisFilter) {
  return diagnosisJson("openings/lines", scopeOf(filter));
}

/**
 * The games of the window, one row each, with their clocks and their opponents: the summary at the top of
 * /analysis, how the clock went, who the opponents were and the hours they were played at, all out of one
 * read (domain/analysisPeriod.ts). Free, like everything that does not ask the engine.
 */
export function fetchDiagnosisPlayed(filter: DiagnosisFilter) {
  return diagnosisJson("played", { ...scopeOf(filter), result: null });
}

/**
 * Where the reader's day happens. Only the browser knows, and the weekly email is written on a server that
 * does not, so the account keeps it: told once, and again whenever the device says something else.
 */
export async function saveTimeZone(zone: string): Promise<void> {
  await fetch("/api/user/timezone", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ zone }),
  });
}

export async function fetchTimeZone(): Promise<string | null> {
  const response = await fetch("/api/user/timezone", { credentials: "include" });
  if (!response.ok) return null;
  const body = await response.json() as { zone: string | null };
  return body.zone;
}

/**
 * What every imported game of the filter says, analysed or not: the record of each opening family, the games
 * of each month and how many were analysed, the losses on the clock. For every result, whatever is asked.
 */
export function fetchDiagnosisImported(filter: DiagnosisFilter) {
  return diagnosisJson("imported", { ...scopeOf(filter), result: null });
}

export function fetchDiagnosisGames(query: DiagnosisGamesQuery) {
  return diagnosisJson("games", { ...query });
}

export function fetchDiagnosisGame(gameId: number) {
  return diagnosisJson(`games/${gameId}`, {});
}

/** Which games to analyse: the last `limit` lost or drawn ones not analysed yet (docs/GAME-DIAGNOSIS.md §4.3). */
export interface DiagnosisScope {
  limit?: number | null;
  since?: string | null;
  until?: string | null;
  speeds?: string[] | null;
  /** LICHESS or CHESS_COM. */
  provider?: string | null;
}

/** The answer of a POST that can be refused for a reason worth showing: the server's message, or the body. */
async function diagnosisPost(path: string, scope: DiagnosisScope) {
  const response = await fetch(`/api/diagnosis/${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(scope),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    return { ok: false as const, status: response.status, message: (body?.message as string) || `HTTP ${response.status}` };
  }
  return { ok: true as const, status: response.status, body };
}

export function fetchDiagnosisStatus() {
  return diagnosisJson("status", {});
}

export function estimateDiagnosis(scope: DiagnosisScope) {
  return diagnosisPost("estimate", scope);
}

export function requestDiagnosis(scope: DiagnosisScope) {
  return diagnosisPost("jobs", scope);
}

export function fetchDiagnosisJob(jobId: number) {
  return diagnosisJson(`jobs/${jobId}`, {});
}

// -------------------------------------------------------------------------------------------------
// The study list (docs/STUDY-LIST.md)
// -------------------------------------------------------------------------------------------------

export async function fetchStudyList(): Promise<StudyListItem[]> {
  const response = await fetch("/api/study-list", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to fetch the study list: HTTP ${response.status}`);
  }
  return response.json();
}

/** Adding what is already there gives back what is there. */
export async function addToStudyList(item: { color: StudyColor; kind: StudyKind; ref: string; source: string }):
    Promise<StudyListItem> {
  const response = await fetch("/api/study-list", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(item),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error((body?.message as string) || `Failed to add it to the study list: HTTP ${response.status}`);
  }
  return response.json();
}

/** Openings by name, every word of the query in it: what to add when the report never named it. */
export async function searchOpenings(query: string): Promise<NamedOpening[]> {
  const response = await fetch(`/api/study-list/openings?q=${encodeURIComponent(query)}`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to search the openings: HTTP ${response.status}`);
  }
  return response.json();
}

export async function removeFromStudyList(id: number) {
  const response = await fetch(`/api/study-list/${id}`, { method: "DELETE", credentials: "include" });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Failed to take it out of the study list: HTTP ${response.status}`);
  }
}

// -------------------------------------------------------------------------------------------------
// The account (/account): what it is, and the name, the password and the emails, which are what can change
// -------------------------------------------------------------------------------------------------

export interface EmailChoice {
  key: string;
  name: string;
  what: string;
  subscribed: boolean;
  /** How often, for the daily training (routine/RoutineCadence); null for the others. */
  cadence: "daily" | "every_other_day" | "weekly" | null;
}

export interface Account {
  email: string;
  displayName: string | null;
  fullName: string | null;
  provider: "GOOGLE" | "LOCAL" | string | null;
  hasPassword: boolean;
  createdAt: string | null;
  timeZone: string | null;
  /** "en" or "es": the language of the site and its emails for this account. */
  language: string;
  emails: EmailChoice[];
}

async function accountCall<T>(path: string, method: string, payload: unknown, fallback: string): Promise<T> {
  const response = await fetch(`/api/account${path}`, {
    method,
    credentials: "include",
    headers: payload === undefined ? undefined : { "Content-Type": "application/json" },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  const body = await response.json().catch(() => null) as { message?: string } | null;
  if (!response.ok) throw new Error(body?.message || `${fallback}: HTTP ${response.status}`);
  return body as T;
}

export function fetchAccount(): Promise<Account> {
  return accountCall<Account>("", "GET", undefined, "Could not read your account");
}

export function saveProfile(displayName: string, fullName: string): Promise<Account> {
  return accountCall<Account>("/profile", "PUT", { displayName, fullName }, "Could not save your name");
}

/** Resolves with the server's message when it changed; throws with the reason when it did not. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<string> {
  const answer = await accountCall<{ message: string }>("/password", "PUT", { currentPassword, newPassword },
    "Could not change your password");
  return answer.message;
}

export function saveEmailChoice(kind: string, subscribed: boolean, cadence: string | null = null): Promise<Account> {
  return accountCall<Account>(`/emails/${encodeURIComponent(kind)}`, "PUT", { subscribed, cadence },
    "Could not save your email choice");
}

/** The language the account reads the site and its emails in. */
export function saveLanguage(language: string): Promise<Account> {
  return accountCall<Account>("/language", "PUT", { language }, "Could not save your language");
}

/** The link to set a password, to the account's own mailbox: how a Google account gets one. */
export async function requestPasswordLink(email: string): Promise<string> {
  const response = await fetch("/api/auth/forgot-password", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const body = await response.json().catch(() => null) as { message?: string } | null;
  if (!response.ok) throw new Error(body?.message || `Could not send the link: HTTP ${response.status}`);
  return body?.message || "The link is on its way.";
}

// ---------------------------------------------------------------------------------------------
// Your positions (docs/POSITIONS.md)
// ---------------------------------------------------------------------------------------------

/** How many moments each exercise has ready, waiting and retired. Null without a session. */
export async function fetchPositionsSummary(): Promise<ExerciseCount[] | null> {
  const response = await fetch("/api/positions/summary", { credentials: "include" });
  if (response.status === 401) return null;
  if (!response.ok) {
    throw new Error(`Failed to read your positions: HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchPositions(exercise: PositionExercise, limit: number,
                                     phase: GamePhase | null = null): Promise<DrillPosition[]> {
  const only = phase ? `&phase=${phase}` : "";
  const response = await fetch(`/api/positions?exercise=${exercise}${only}&limit=${limit}`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Failed to read your positions: HTTP ${response.status}`);
  }
  return response.json();
}

/** One attempt at a moment, which decides when it comes back. */
export async function recordPositionAttempt(attempt: {
  gameId: number; ply: number; exercise: PositionExercise; result: DrillResult;
  moves: {
    san: string; uci: string; cp: number | null; bestSan: string | null; bestCp: number | null; loss: number | null;
    ms: number | null; reply: string | null;
  }[];
  endCp: number | null; tookBack: boolean;
}): Promise<void> {
  const response = await fetch("/api/positions/attempts", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(attempt),
  });
  if (!response.ok) {
    throw new Error(`Failed to keep the attempt: HTTP ${response.status}`);
  }
}

/**
 * A move played where the user may have a repertoire (docs/REPERTOIRES.md). The server checks it against the
 * repertoire and keeps it when the repertoire has an answer there; `keepalive` so a move played just before the
 * page is left still arrives.
 */
export async function recordRepertoireAttempt(attempt: {
  source?: "study" | "drill"; sessionId: string; attemptIndex: number; startFen: string | null; path: string[];
  uci: string; hinted: boolean;
}): Promise<{ inRepertoire: boolean; expectedUci: string[] } | null> {
  const response = await fetch("/api/repertoire/attempts", {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(attempt),
  });
  if (!response.ok) {
    throw new Error(`Failed to keep the repertoire attempt: HTTP ${response.status}`);
  }
  // 204: the repertoire has no move there any more (a file turned off since the review was read)
  return response.status === 204 ? null : response.json();
}

export interface RepertoireReviewRow {
  fen: string;
  color: "white" | "black";
  pathUci: string;
  startFen: string | null;
  opening: string | null;
  repertoireId: number | null;
  repertoireName: string | null;
  expectedUci: string[];
  seen: number;
  right: number;
  misses: number;
  streak: number;
  status: "review" | "relearnt" | "known";
  lastAt: string;
  lastMissAt: string | null;
  lastMissUci: string | null;
  /** Whether it is asked again now; when it will be otherwise. */
  ready: boolean;
  nextAt: string | null;
  /** Of the attempts, the moves of their games; the ones that left the repertoire, and the points of those games. */
  gameSeen: number;
  gameMisses: number;
  gameMissPoints: number;
  lastMissSource: "study" | "drill" | "game" | null;
}

export interface RepertoireReview {
  positions: number;
  review: number;
  ready: number;
  relearnt: number;
  known: number;
  /** Moves of their games checked against the repertoire, and the repertoires in use. */
  gameMoves: number;
  repertoires: number;
  rows: RepertoireReviewRow[];
}

/** Every position of the repertoire played in, the forgotten ones first. */
export async function fetchRepertoireReview(): Promise<RepertoireReview | null> {
  const response = await fetch("/api/repertoire/review", { credentials: "include" });
  if (response.status === 401) return null;
  if (!response.ok) {
    throw new Error(`Failed to read the repertoire review: HTTP ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------------------------
// The AI coach (docs/COACH.md): its conversations, admins only while it is tried out
// ---------------------------------------------------------------------------------------------

export interface CoachConversation {
  id: number;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface CoachMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  /** The tools the coach read to say it, comma separated; only on its own messages. */
  tools: string | null;
  costUsd: number | null;
  createdAt: string;
}

export interface CoachExchange {
  conversationId: number;
  asked: CoachMessage;
  answer: CoachMessage;
}

export async function fetchCoachConversations(): Promise<CoachConversation[]> {
  const response = await fetch("/api/coach/conversations", { credentials: "include" });
  if (!response.ok) throw new Error(`Failed to read the conversations: HTTP ${response.status}`);
  return response.json();
}

export async function fetchCoachConversation(id: number): Promise<{ conversation: CoachConversation; messages: CoachMessage[] }> {
  const response = await fetch(`/api/coach/conversations/${id}`, { credentials: "include" });
  if (!response.ok) throw new Error(`Failed to read the conversation: HTTP ${response.status}`);
  return response.json();
}

/** Asks the coach: in a conversation of its own when `conversationId` is null, which the answer then names. */
export async function askCoach(conversationId: number | null, text: string): Promise<CoachExchange> {
  const response = await fetch(conversationId === null
    ? "/api/coach/conversations" : `/api/coach/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!response.ok) throw new Error(`The coach did not answer: HTTP ${response.status}`);
  return response.json();
}
